import { Injectable, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import {
  NativeBiometric,
  BiometryType,
  AuthenticationStrength,
  BiometricAuthError,
  AvailableResult,
  Credentials
} from '@capgo/capacitor-native-biometric';
import { IsRoot } from '@capgo/capacitor-is-root';
import { TranslateService } from '@ngx-translate/core';
import { CoreBiometricData } from './core.models';

/**
 * Keychain (iOS) / keystore (Android) "server" identifier under which the
 * biometric login credentials are stored. Single source of truth — must match
 * the app bundle id (`com.bepatient.smartapp`).
 */
export const BIOMETRIC_SERVER_ID = 'com.bepatient.smartapp';

/** localStorage key holding the persisted {@link CoreBiometricData} flags. */
export const BIOMETRIC_STORAGE_KEY = 'deviceBiometric';

/** Why a biometric credential teardown was triggered (drives UX/telemetry). */
export type BiometricDisableReason = 'toggle' | 'rooted' | 'changeInstance';

/**
 * The plugin's web stub reports `isAvailable: true` regardless of the
 * browser; this is what {@link BiometricService.isBiometricAvailable} returns
 * instead on non-native platforms.
 */
const NOT_AVAILABLE_RESULT: AvailableResult = {
  isAvailable: false,
  authenticationStrength: AuthenticationStrength.NONE,
  biometryType: BiometryType.NONE,
  deviceIsSecure: false,
  strongBiometryIsAvailable: false,
  errorCode: BiometricAuthError.BIOMETRICS_UNAVAILABLE
};

/**
 * Single owner of every biometric concern: native availability + prompt,
 * root/jailbreak detection, keychain credential storage, and the persisted
 * `deviceBiometric` flags. Previously these primitives lived scattered inside
 * {@link CoreUtilsService}; they are consolidated here.
 */
@Injectable()
export class BiometricService {
  private translate = inject(TranslateService);

  /** Prevents overlapping `verifyIdentity()` prompts (native throws otherwise). */
  private isBiometricPromptActive = false;

  // --- Native availability & prompt -----------------------------------------

  /**
   * Check if biometric is available on the device.
   * iPhone X and above return 'face'; other iOS/Android devices return 'finger';
   * Android P+ returns 'biometric'.
   *
   * The plugin's web stub reports `isAvailable: true` regardless of the
   * browser, so non-native platforms are gated out here rather than relying
   * on every call site to remember to check `Capacitor.isNativePlatform()`.
   */
  isBiometricAvailable(): Promise<AvailableResult> {
    if (!Capacitor.isNativePlatform()) {
      return Promise.resolve(NOT_AVAILABLE_RESULT);
    }
    return NativeBiometric.isAvailable({ useFallback: false });
  }

  /**
   * Show the native biometric authentication prompt.
   * @param useFallback allow the OS PIN/pattern/passcode fallback
   * @returns the plugin result on success; rejects with the plugin error code otherwise
   */
  showBiometricPrompt(useFallback = false): Promise<any> {
    // The web stub resolves verifyIdentity() unconditionally with no real
    // check performed, so it must never be trusted as a genuine confirmation.
    if (!Capacitor.isNativePlatform()) {
      return Promise.reject({
        code: 0,
        message: 'Biometric not available on this platform'
      });
    }
    if (this.isBiometricPromptActive) {
      return Promise.reject({ code: 15, message: 'Biometric already active' });
    }
    this.isBiometricPromptActive = true;
    return NativeBiometric.verifyIdentity({
      title: this.translate.instant('BIOMETRIC.SHOW_PROMPT.TITLE'),
      subtitle: this.translate.instant('BIOMETRIC.SHOW_PROMPT.SUBTITLE'),
      description: this.translate.instant('BIOMETRIC.SHOW_PROMPT.DESCRIPTION'),
      useFallback,
      maxAttempts: 5 // avoid premature lockout/timeout
    })
      .then(result => {
        this.isBiometricPromptActive = false;
        return result;
      })
      .catch(error => {
        this.isBiometricPromptActive = false;
        throw error; // ensure rejection is caught by callers
      });
  }

  // --- Root / jailbreak detection -------------------------------------------

  /**
   * `true` only when the device is definitively rooted/jailbroken.
   *
   * Non-native platforms and any plugin error resolve to `false` (fail-open) so
   * a detection hiccup never mass-disables the convenience feature — a genuine
   * `true` is the only thing that turns biometric off. Password login is always
   * available regardless, so a false negative here never blocks access.
   */
  async isDeviceRooted(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }
    try {
      const { result } = await IsRoot.isRooted();
      return !!result;
    } catch (error) {
      console.warn(
        'IsRoot.isRooted() failed; treating device as not rooted:',
        error
      );
      return false;
    }
  }

  // --- Keychain credential storage ------------------------------------------

  /**
   * Register credentials to be used for biometric login.
   */
  registerBiometricSecret({ username, password }): Promise<any> {
    return NativeBiometric.setCredentials({
      username,
      password,
      server: BIOMETRIC_SERVER_ID
    });
  }

  /**
   * Load the credentials registered by {@link registerBiometricSecret}.
   */
  loadBiometricSecret(): Promise<Credentials> {
    return NativeBiometric.getCredentials({ server: BIOMETRIC_SERVER_ID });
  }

  /**
   * Remove the credentials from keychain/keystore. Resolves even when nothing
   * was stored and never rejects, so it is safe to `await` before re-register.
   */
  removeBiometricCredential(): Promise<void> {
    return this.loadBiometricSecret()
      .then(data => {
        if (data && data.username && data.password) {
          return NativeBiometric.deleteCredentials({
            server: BIOMETRIC_SERVER_ID
          }).then(() => void 0);
        }
        return void 0;
      })
      .catch(() => void 0);
  }

  /**
   * Tear down biometric login: delete the stored secret and persist an
   * off state. Used when the user turns the toggle off, when a rooted device
   * is detected, and when the active instance changes.
   *
   * Uses `enableBiometricLogin: 'false'` (not `'disabled'`) so the state does
   * not auto-recover on the next launch; `deviceRooted` records the reason.
   *
   * `userOptedOut` is set ONLY for a deliberate `'toggle'` off, so the app
   * stops auto-prompting for that user. A `'rooted'` teardown or a
   * `'changeInstance'` reset clears it — a rooted device is re-checked at
   * runtime, and a new instance means a new (undecided) user.
   */
  async disableBiometric(reason?: BiometricDisableReason): Promise<void> {
    await this.removeBiometricCredential();
    const existing = this.getDeviceBiometricData();
    const next: CoreBiometricData = {
      biometricType: 'none',
      biometricEnrolled: 'false',
      ...(existing ?? {}),
      enableBiometricLogin: 'false',
      keychainEnrolled: 'false',
      deviceRooted: reason === 'rooted',
      userOptedOut: reason === 'toggle'
    };
    this.setDeviceBiometricData(next);
  }

  // --- Persisted flags (localStorage) ---------------------------------------

  /**
   * Return the biometric data persisted in localStorage.
   */
  getDeviceBiometricData(): CoreBiometricData | undefined {
    const raw = localStorage.getItem(BIOMETRIC_STORAGE_KEY);
    return (raw && JSON.parse(raw)) || void 0;
  }

  /**
   * Persist the biometric data to localStorage.
   */
  setDeviceBiometricData(data: CoreBiometricData): void {
    localStorage.setItem(BIOMETRIC_STORAGE_KEY, JSON.stringify(data));
  }

  /**
   * Drop the persisted biometric flags entirely (used on first Capacitor run).
   */
  clearDeviceBiometricData(): void {
    localStorage.removeItem(BIOMETRIC_STORAGE_KEY);
  }

  // --- Type / error mapping -------------------------------------------------

  /**
   * Map the plugin {@link BiometryType} enum to the app's biometric type label.
   */
  getBiometricType(biometricType: number): string {
    let authType: string = void 0;

    switch (biometricType) {
      case BiometryType.FACE_ID:
        authType = 'face';
        break;

      case BiometryType.TOUCH_ID:
        authType = 'touch';
        break;

      case BiometryType.FACE_AUTHENTICATION:
        authType = 'face';
        break;

      case BiometryType.FINGERPRINT:
        authType = 'touch';
        break;

      case BiometryType.IRIS_AUTHENTICATION:
        authType = 'biometric';
        break;

      case BiometryType.MULTIPLE:
        authType = 'biometric';
        break;

      case BiometryType.NONE:
        authType = 'none';
        break;

      default:
        authType = 'biometric';
        break;
    }

    return authType;
  }

  /**
   * Parse a biometric plugin error into a translated, user-friendly message.
   */
  parseBiometricError(error): { code: string | number; message: string } {
    if (error && !error.code) {
      return {
        code: void 0,
        message: this.translate.instant('BIOMETRIC.ERROR.AUTH_FAILED')
      };
    }

    let errorMessage = '';
    switch (error.code) {
      case 10:
        errorMessage = this.translate.instant('BIOMETRIC.ERROR.AUTH_FAILED');
        break;

      case 11:
        errorMessage = this.translate.instant(
          'BIOMETRIC.ERROR.AUTH_DISSMISSED'
        );
        break;

      case 14:
        errorMessage = this.translate.instant('BIOMETRIC.ERROR.NOT_REGISTERED');
        break;

      case 15:
        errorMessage = this.translate.instant(
          'BIOMETRIC.ERROR.AUTH_DISSMISSED'
        );
        break;

      case 16:
        errorMessage = this.translate.instant(
          'BIOMETRIC.ERROR.AUTH_DISSMISSED'
        );
        break;

      case 17:
        errorMessage = this.translate.instant(
          'BIOMETRIC.ERROR.AUTH_DISSMISSED'
        );
        break;

      case 0:
      case 12:
      case 13:
      default:
        errorMessage = this.translate.instant('BIOMETRIC.ERROR.AUTH_ERROR');
        break;
    }

    return {
      message: errorMessage,
      code: error.code || -1
    };
  }

  /**
   * Build the translated "device is compromised" failure payload used when a
   * rooted/jailbroken device is detected.
   */
  deviceCompromisedError(): { code: string | number; message: string } {
    return {
      code: -1,
      message: this.translate.instant('BIOMETRIC.ERROR.DEVICE_COMPROMISED')
    };
  }
}
